import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';

import { SitterSearchCard } from 'us-react-components/dist/SitterSearchCard/SitterSearchCard';
import { namespaces, useSharedTranslation } from 'us-react-components/dist/hooks/useSharedTranslation';
import { useSitterSearchCardHandlers } from 'us-react-components/dist/hooks/useSitterSearchCardHandlers';
import {
  PagedResponseMeta,
  Sitter,
  SitterService,
} from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { SitterProfileChrome } from '../SitterReviews/components/SitterProfileChrome';

// Lists caregivers connected to the viewed sitter. Paginated via `More
// Caregivers`; hidden sitters are filtered out client-side.
export const SitterFriends = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores, services: { tracker } } = useDependencies();
  const user = stores.user.user;

  const viewedSitterId = pathId ? Number(pathId) : undefined;

  const [viewedSitter, setViewedSitter] = useState<Sitter | null>(null);
  const [friends, setFriends] = useState<Sitter[]>([]);
  const [meta, setMeta] = useState<PagedResponseMeta | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    document.title = 'UrbanSitter - Caregiver Friends';
  }, []);

  useEffect(() => {
    if (!user?.id || !viewedSitterId) return;
    let cancelled = false;

    const load = async (): Promise<void> => {
      try {
        const resp = await SitterService.get(viewedSitterId, {
          params: { parentId: user.id, userId: user.id },
        });
        if (cancelled) return;
        setViewedSitter(resp.data?.data ?? null);
      } catch (err) {
        console.warn('[SitterFriends] sitter load failed', err);
      }
    };
    void load();

    return (): void => {
      cancelled = true;
    };
  }, [user?.id, viewedSitterId]);

  const fetchFriends = async (pageToFetch: number): Promise<void> => {
    if (!user?.id || !viewedSitterId) return;
    const isFirst = pageToFetch === 1;
    if (isFirst) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const resp = (
        await SitterService.getByFilter({
          params: {
            friendsOf: viewedSitterId,
            parentId: user.id,
            userId: user.id,
            page: pageToFetch,
          },
        })
      ).data;
      const visible = (resp.data ?? []).filter(
        (s) => !(s as Sitter & { hidden?: boolean }).hidden,
      );
      setFriends((prev) => (isFirst ? visible : [...prev, ...visible]));
      setMeta(resp.meta);
      setPage(resp.meta?.page ?? pageToFetch);
    } catch (err) {
      console.warn('[SitterFriends] friends load failed', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!user?.id || !viewedSitterId) return;
    fetchFriends(1);

  }, [user?.id, viewedSitterId]);

  const { handleCardClick, handleToggleFavorite } = useSitterSearchCardHandlers({
    userId: user?.id,
    track: tracker.track.bind(tracker),
    navigateToSitter: (id) => navigate(`/sitter/${id}`),
    logPrefix: 'CaregiverSitterFriends',
  });

  const onToggleFavorite = async (sitter: Sitter): Promise<void> => {
    const result = await handleToggleFavorite(sitter);
    if (!result) return;
    setFriends((prev) => prev.map((s) => (s.id === sitter.id
      ? { ...s, relationship: { ...s.relationship, bookmarked: result.bookmarked, favorite: result.favorite } }
      : s)));
  };

  const firstName = viewedSitter?.firstName ?? '';
  const hasMore = !!meta?.size && friends.length < meta.size;

  return (
    <div className='container py-6 max-w-6xl mx-auto'>
      <SitterProfileChrome sitter={viewedSitter} activeTab='friends' viewerUserId={user?.id} />

      {loading ? (
        <p className='text-neutral-600'>{t('common.loading')}</p>
      ) : friends.length > 0 ? (
        <>
          <h1 className='text-2xl font-semibold mb-6'>
            {t('pages.sitterFriends.heading', {
              name: firstName,
              count: meta?.size ?? friends.length,
            })}
          </h1>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            {friends.map((friend) => (
              <SitterSearchCard
                key={friend.id}
                sitter={friend}
                userPictureUrl={user?.pictureUrl}
                onCardClick={handleCardClick}
                onToggleFavorite={onToggleFavorite}
              />
            ))}
          </div>
          {hasMore && (
            <div className='text-center mt-6'>
              <Button
                buttonType={ButtonType.secondary}
                disabled={loadingMore}
                onClick={(): void => { fetchFriends(page + 1); }}
              >
                {loadingMore ? t('common.loading') : t('pages.sitterFriends.loadMore')}
              </Button>
            </div>
          )}
        </>
      ) : (
        <h3 className='text-lg'>
          {t('pages.sitterFriends.empty', { name: firstName })}
        </h3>
      )}
    </div>
  );
});
